// Entry point for the kolistat web editor: wires CodeMirror 6, the WASM
// lexer/parser bridge, folder import, the toolbar (run/reset/theme),
// the resizable sidebar, and the batch "parse all" launcher.

// Statistical-Report faces: IBM Plex Mono for everything, Source
// Serif 4 for lesson prose (see styles.css --font-mono/--font-serif).
import '@fontsource/ibm-plex-mono/400.css';
import '@fontsource/ibm-plex-mono/400-italic.css';
import '@fontsource/ibm-plex-mono/600.css';
import '@fontsource/source-serif-4/400.css';
import '@fontsource/source-serif-4/600.css';
// bedevere-wise component styles (chart / spreadsheet / stats panel).
// Imported eagerly — not with the lazy chunks — so its global rules
// load in a deterministic order that styles.css's specificity bumps
// are audited against, and so the first plot doesn't restyle mid-run.
import '@kolistat/bedevere-wise/style.css';

import { Compartment, EditorState } from '@codemirror/state';
import { EditorView, lineNumbers, highlightActiveLine, keymap } from '@codemirror/view';
import { defaultKeymap, history, historyKeymap, toggleComment, indentWithTab } from '@codemirror/commands';
import { searchKeymap } from '@codemirror/search';
import { lintKeymap } from '@codemirror/lint';

import { loadKolilang } from './wasm';
import { bindKolilang, ensureParse } from './parse-cache';
import { sasLanguage } from './sas-lang';
import { sasLinter } from './linter';
import {
  pickFolder,
  DragDropSource,
  DirectoryHandleSource,
  loadRootHandle,
  saveRootHandle,
  clearRootHandle,
  queryReadPermission,
  type FolderSource,
  type FileEntry,
} from './fs';
import { OPEN_EVENT, renderFileTree, type FileTreeOptions, type OpenEventDetail } from './file-tree';
import { attachDataSource, ensureDataLoaded, isDataLoaded } from './data-files';
import { formatSize, readerFor } from './data-kinds';
import { runBatch } from './batch';
import { attachOutputPanel, runProgram, resetRunSession, isRunInFlight } from './runner';
import {
  applyInitialTheme,
  attachThemeSync,
  attachToolbar,
  editorThemeFor,
  type Theme,
  type ToolbarApi,
} from './toolbar';
import { attachTutorial } from './tutorial';
import { TabManager, type TabInfo } from './tabs';
import { saveTab, ensureSasName, type SaveOutcome } from './save';
import { promptFilename } from './name-modal';
import { showToast } from './toast';
import * as commands from './commands';
import {
  captureAuthErrorFromUrl,
  captureTokenFromUrl,
  getUser,
  isAuthenticated,
  onAuthStateChange,
  signOut,
  startAuthLifecycle,
  type AuthErrorInfo,
} from './auth';
import { closeLoginModal, openLoginModal, showLoginError } from './login-modal';
import { maybePromptTerms, requireTermsAccepted, termsPending } from './terms-modal';
import { openAboutModal } from './about-modal';
import {
  captureDeepLinkFromUrl,
  buildShareUrl,
  stashDeepLink,
  takeStashedDeepLink,
  SHARE_URL_MAX,
} from './deep-link';
import { attachWorkLibrary, refreshWorkLibrary } from './work-library';

// Boot order: read the `#code=...` deep link FIRST, because
// `captureTokenFromUrl` drops the whole fragment — `captureDeepLinkFromUrl`
// strips only its own keys, so a fragment carrying both a token and a
// `#code` leaves the token intact for the auth capture that follows.
// We hold the result and apply it once the scratch tab + engine exist.
const urlDeepLink = captureDeepLinkFromUrl();

// Drain a `#token=...` redirect handoff into storage before anything
// else reads auth state. Doing this synchronously at the top of the
// module guarantees `getLicenseContext` (used per-run) and `getUser`
// (used by the toolbar render) see the just-arrived token on the very
// first paint after a magic-link or OAuth round-trip. SEC-011: the
// token lives in the URL fragment so it never reaches our access logs.
// Run error capture after token capture so a redirect carrying both
// (unusual but defensible) scrubs both surfaces.
captureTokenFromUrl();
const authError: AuthErrorInfo | null = captureAuthErrorFromUrl();

// A deep link opened while signed out cannot run yet, and the sign-in
// leaves the page (OAuth) or lands in a new tab (magic link). Keep it
// for the signed-in boot that follows. A signed-in boot takes the kept
// link back unless the URL carries a newer one.
const deepLink = (() => {
  if (!isAuthenticated()) {
    if (urlDeepLink) stashDeepLink(urlDeepLink, localStorage, Date.now());
    return urlDeepLink;
  }
  const kept = takeStashedDeepLink(localStorage, Date.now());
  return urlDeepLink ?? kept;
})();

const placeholder = `/* kolistat — SAS editor.
   Parser diagnostics are live; click ▶ run to execute against
   in-browser DuckDB-WASM. PROC SQL, DATA step (SQL-route),
   PROC PRINT/SORT, TITLE all run end-to-end today. */

proc sql;
    create table work.class as
        select 'Alice' as name, 14 as age, 62 as height
        union all select 'Bob',   16, 70
        union all select 'Carol', 12, 55
        union all select 'Alice', 14, 62  /* duplicate */
        union all select 'Dan',   19, 68
        union all select 'Eve',   15, 64;
quit;

data work.tall;
    set work.class;
    where height > 60;
    keep name age height;
run;

title "Tall students, sorted by age descending";
proc sort data=work.tall out=work.tall_sorted;
    by descending age name;
run;

proc print data=work.tall_sorted;
run;

title "Unique names only";
proc sort data=work.class out=work.unique nodupkey;
    by name;
run;

proc print data=work.unique;
run;
`;

const initialTheme: Theme = applyInitialTheme();

const kolilang = await loadKolilang();
bindKolilang(kolilang);
const version = kolilang.version();
console.log(`[kolilang] ${version} loaded`);

const countsEl = document.getElementById('status-counts');
const crumbsEl = document.getElementById('status-crumbs');
const versionEl = document.getElementById('status-version');

// Pull the bare semver out of the engine's version string
// ("kolilang 0.1.0-wasm" → "0.1.0") so the status-bar label tracks
// future bumps without a hardcode. Falls back to the raw string if the
// shape ever changes.
if (versionEl) {
  const semver = version.match(/\d+\.\d+\.\d+/)?.[0];
  versionEl.textContent = semver ? `kolilang v${semver}` : version;
}

commands.register({ id: 'about', label: 'About and legal', run: () => openAboutModal(version) });

function updateStatus(doc: string): void {
  try {
    const parse = ensureParse(doc);
    if (countsEl) {
      countsEl.textContent =
        `${parse.stmtCount} stmts · ${parse.diagCount} warn · ${parse.tokens} tokens`;
    }
  } catch (e) {
    if (countsEl) countsEl.textContent = `error: ${e instanceof Error ? e.message : String(e)}`;
  }
}

const themeCompartment = new Compartment();

/// The editor needs an account. True when signed in; otherwise the
/// required sign-in dialog opens and the caller must not run.
function ensureSignedIn(): boolean {
  if (isAuthenticated()) return true;
  openLoginModal('required');
  return false;
}

/// The engine refused a procedure this run, which means the session
/// lapsed between the sign-in check and the run. The error already
/// rendered in the output panel. A signed-out user gets the sign-in
/// dialog; a signed-in user who has not accepted the current terms
/// gets the terms dialog.
function onRunBlocked(): void {
  if (!isAuthenticated()) {
    openLoginModal('required');
    return;
  }
  void requireTermsAccepted();
}

/// Run the current selection through `kolistat.run`, or the whole doc if
/// the selection is empty. Returns true so CodeMirror's keymap chain
/// stops here — without the return-true the default Enter handler
/// would still insert a newline before our preventDefault landed.
function runFromEditor(target: EditorView): boolean {
  if (!ensureSignedIn()) return true;
  if (isRunInFlight()) {
    showToast({ message: 'A run is already in progress.', durationMs: 2500 });
    return true;
  }
  const sel = target.state.selection.main;
  const source = sel.empty
    ? target.state.doc.toString()
    : target.state.sliceDoc(sel.from, sel.to);
  void runProgram(kolilang, source, output, onRunBlocked)
    .catch((e) => {
      output.error(`runner failed: ${e instanceof Error ? e.message : String(e)}`);
    })
    .finally(() => void refreshWorkLibrary());
  return true;
}

// Forward-declared so the keymap below can call save before onSave /
// onSaveAs are defined further down (in the toolbar wiring section).
let saveActiveTab: () => void = () => {};
let saveActiveTabAs: () => void = () => {};

function saveFromEditor(): boolean {
  saveActiveTab();
  return true;
}

function saveAsFromEditor(): boolean {
  saveActiveTabAs();
  return true;
}

const editorActionsKeymap = [
  { key: 'Mod-Enter', run: runFromEditor, preventDefault: true },
  { key: 'Mod-/', run: toggleComment },
  // Ctrl+S — preventDefault stops the browser's "Save Page As" from
  // hijacking the keystroke. Wired through the editor keymap (not a
  // document listener) so it doesn't fire when focus is elsewhere.
  { key: 'Mod-s', run: saveFromEditor, preventDefault: true },
  // Ctrl+Shift+S — Save As (always prompts for a name).
  { key: 'Mod-Shift-s', run: saveAsFromEditor, preventDefault: true },
  // `indentWithTab` makes Tab indent the current line (or selected
  // block) instead of moving keyboard focus out of the editor.
  // Shift+Tab dedents. Standard SAS-editor behaviour.
  indentWithTab,
];

// Single EditorView for the whole page; per-tab EditorState gets
// swapped in via `view.setState(...)` (driven by TabManager). The
// initial state is a placeholder — tabs.restore() or the fallback
// scratch tab below replaces it before the user sees anything.
const parent = document.getElementById('editor-host');
if (!parent) throw new Error('kolistat: #editor-host not found');
const view = new EditorView({
  state: EditorState.create({ doc: '' }),
  parent,
});

const outputHost = document.getElementById('output-host');
if (!outputHost) throw new Error('kolistat: #output-host not found');
const output = attachOutputPanel(outputHost);
output.log('ready — click ▶ run to execute against DuckDB-WASM.');

// Work-library sidebar section: renders its empty state now, fills
// after each run (see the refreshWorkLibrary calls on the run paths).
const workLibEl = document.getElementById('work-library');
if (workLibEl) attachWorkLibrary(workLibEl);

if (crumbsEl) crumbsEl.textContent = 'no folder picked';

const applyEditorTheme = attachThemeSync(view, themeCompartment);

// Forward-declared so the editor's docChanged listener can fire the
// tab manager's dirty-dot refresh. tabs is initialised below.
let tabs!: TabManager;
let currentTheme: Theme = initialTheme;

// Build a fresh EditorState carrying every extension a tab needs.
// Called per new tab + once per restored tab on reload.
function buildEditorState(source: string): EditorState {
  return EditorState.create({
    doc: source,
    extensions: [
      lineNumbers(),
      highlightActiveLine(),
      history(),
      keymap.of([...editorActionsKeymap, ...defaultKeymap, ...historyKeymap, ...searchKeymap, ...lintKeymap]),
      themeCompartment.of(editorThemeFor(currentTheme)),
      sasLanguage(kolilang),
      sasLinter(kolilang),
      EditorView.updateListener.of((u) => {
        if (u.docChanged) {
          updateStatus(u.state.doc.toString());
          tabs?.notifyDocChanged();
        }
      }),
    ],
  });
}

const tabBarEl = document.getElementById('tab-bar');
if (!tabBarEl) throw new Error('kolistat: #tab-bar not found');

// Hoisted above the TabManager so onActiveChange can read them on
// the initial activation that fires during construction.
let currentSource: FolderSource | null = null;
let activePath: string | null = null;
let entriesCache: FileEntry[] = [];
const treeEl = document.getElementById('file-tree');

/// Local sources hold their files, so no loaded dot is drawn. A source
/// without `localFile` loads data on demand and shows the dot state.
function treeOpts(): FileTreeOptions {
  return currentSource?.localFile ? {} : { loaded: isDataLoaded };
}

// Resolved by `attachTutorial` (async); calls before then are dropped,
// since on init the tutorial's own restore-active-lesson dance handles
// the rail anyway.
let tutorialApi:
  | { notifyActiveTab(id: string | null): void; openFirst(): Promise<void> }
  | null = null;
let toolbarApi: ToolbarApi | null = null;

const FILE_TAB_PREFIX = 'file:';
const UNTITLED_TAB_PREFIX = 'untitled:';

// Stashed file handles from `showSaveFilePicker` for tabs whose saved
// destination is OUTSIDE the currently-loaded folder. Repeated saves
// of those tabs write through the stashed handle without re-prompting.
// Keyed by tab id; entries cleared when the tab is closed/renamed.
const tabFileHandles = new Map<string, FileSystemFileHandle>();

tabs = new TabManager({
  view,
  barEl: tabBarEl,
  buildState: buildEditorState,
  onActiveChange: (tab: TabInfo | null) => {
    updateStatus(view.state.doc.toString());
    if (crumbsEl && tab) crumbsEl.textContent = tab.title;
    // File tabs use `file:<path>`; anything else clears the highlight.
    const next = tab?.id.startsWith(FILE_TAB_PREFIX)
      ? tab.id.slice(FILE_TAB_PREFIX.length)
      : null;
    if (next !== activePath) {
      activePath = next;
      if (treeEl && currentSource) {
        renderFileTree(treeEl, currentSource, entriesCache, activePath, treeOpts());
      }
    }
    // Mirror the active tab into the tutorial rail too. Lesson tabs
    // highlight their rail item; non-lesson tabs clear it.
    tutorialApi?.notifyActiveTab(tab?.id ?? null);
    // Save is meaningless for lesson tabs (read-only by policy).
    if (tab?.origin === 'lesson') {
      toolbarApi?.setSaveEnabled(false, 'Lessons are read-only — use New to copy out.');
    } else {
      toolbarApi?.setSaveEnabled(true);
    }
  },
  // Newly-activated state was built with whatever theme was current
  // at buildState time. If the page theme changed since, bump it
  // back into line via the compartment reconfigure.
  onActivated: () => applyEditorTheme(currentTheme),
  onRenameRequest: (id) => void onRenameTab(id),
  // Clearing the stash on close (not just on promotion) stops handles
  // for closed-then-recreated tab ids from leaking — a re-opened
  // scratch with the same id should re-prompt, not silently write to
  // the old destination.
  onClose: (id) => { tabFileHandles.delete(id); },
});

if (!tabs.restore()) {
  tabs.openOrFocus('scratch', 'untitled.sas', 'scratch', placeholder);
}

// Deep-link prefill: drop the shared SAS into the scratch tab the
// editor boots on, replacing its contents (placeholder, or whatever
// was restored under the `scratch` id) and focusing it. `run=1` is
// honoured later, once the engine is ready (see the autorun block
// after the toolbar wiring). Other restored tabs are left untouched.
if (deepLink) {
  tabs.openOrFocus('scratch', 'untitled.sas', 'scratch', deepLink.code, true);
}

// --- folder import + toolbar wiring ---------------------------------------

const toolbarEl = document.getElementById('toolbar');

async function onPickFolder(): Promise<void> {
  const src = await pickFolder();
  if (src) await loadSource(src);
}

function onParseAll(): void {
  if (!currentSource) {
    alert('Pick a folder first, then click "parse all" to run the parser on every .sas file and export the JSON report.');
    return;
  }
  void runBatch(kolilang, currentSource, openFileWithDiag);
}

async function onRun(): Promise<void> {
  if (!ensureSignedIn()) return;
  try {
    await runProgram(kolilang, view.state.doc.toString(), output, onRunBlocked);
  } catch (e) {
    output.error(`runner failed: ${e instanceof Error ? e.message : String(e)}`);
  } finally {
    void refreshWorkLibrary();
  }
}

async function onReset(): Promise<void> {
  output.clear();
  try {
    await resetRunSession();
    output.log('session reset — WORK schema cleared.');
  } catch (e) {
    output.error(`reset failed: ${e instanceof Error ? e.message : String(e)}`);
  } finally {
    void refreshWorkLibrary();
  }
}

// Build a "Run this in k0-lang →" link for the current buffer and copy
// it to the clipboard. The SAS rides the URL fragment (never the query)
// so it's not logged server-side — see deep-link.ts. Programs past
// SHARE_URL_MAX won't survive most address bars / chat apps, so we warn
// and skip the copy rather than hand back a link that silently truncates.
async function onCopyShareLink(): Promise<void> {
  const source = view.state.doc.toString();
  const url = buildShareUrl(source);
  if (url.length > SHARE_URL_MAX) {
    showToast({
      message: `Program too long to share as a link (${url.length} chars). Save the .sas and share the file instead.`,
      durationMs: 6000,
    });
    return;
  }
  try {
    await navigator.clipboard.writeText(url);
    showToast({ message: 'Shareable link copied — it prefills and runs this program.', durationMs: 4000 });
  } catch {
    // Clipboard API can reject (permissions, insecure context, no user
    // gesture). Surface the URL in a sticky toast so the user can select
    // + copy it by hand.
    showToast({
      message: `Could not access the clipboard. Copy this link manually:\n${url}`,
      actions: [{ label: 'Dismiss', onClick: () => {} }],
    });
  }
}

function nextUntitledId(): string {
  let max = 0;
  for (const t of tabs.list()) {
    if (!t.id.startsWith(UNTITLED_TAB_PREFIX)) continue;
    const n = parseInt(t.id.slice(UNTITLED_TAB_PREFIX.length), 10);
    if (Number.isFinite(n) && n > max) max = n;
  }
  return `${UNTITLED_TAB_PREFIX}${max + 1}`;
}

function onNew(): void {
  const id = nextUntitledId();
  const n = id.slice(UNTITLED_TAB_PREFIX.length);
  tabs.openOrFocus(id, `untitled-${n}.sas`, 'scratch', '');
}

// Apply a save outcome to tab state + the output log. Shared by Save
// and Save As so the wrote-picker → file: promotion and the
// outside-folder stash behave identically on both paths.
async function applySaveOutcome(tab: TabInfo, text: string, outcome: SaveOutcome): Promise<void> {
  switch (outcome.kind) {
    case 'wrote-folder':
      tabs.markSaved(tab.id, text);
      output.log(`saved: ${outcome.path}`);
      break;
    case 'wrote-handle':
      tabs.markSaved(tab.id, text);
      output.log(`saved: ${outcome.suggestedName}`);
      break;
    case 'wrote-picker':
      tabs.markSaved(tab.id, text);
      if (outcome.rel) {
        // Picked file lives under the active folder root — promote
        // the tab to a `file:<rel>` id so future saves go through
        // the source and the tree highlight tracks it.
        const newId = `${FILE_TAB_PREFIX}${outcome.rel}`;
        const title = outcome.rel.split('/').pop() ?? outcome.rel;
        tabs.rename(tab.id, newId, title);
        tabFileHandles.delete(tab.id);
        // Refresh the tree in case the picked file is brand new on disk.
        if (currentSource) {
          entriesCache = await currentSource.list();
          await attachDataSource(currentSource, entriesCache);
          if (treeEl) renderFileTree(treeEl, currentSource, entriesCache, activePath, treeOpts());
        }
        output.log(`saved: ${outcome.rel}`);
      } else {
        // Outside the folder — stash the handle so re-save goes
        // straight through.
        tabFileHandles.set(tab.id, outcome.fileHandle);
        output.log(`saved: ${outcome.suggestedName}`);
      }
      break;
    case 'downloaded':
      tabs.markSaved(tab.id, text);
      output.log(`downloaded: ${outcome.suggestedName}`);
      break;
    case 'cancelled':
      // User aborted picker — silently leave the tab dirty.
      break;
    case 'refused':
      output.log(`save refused: ${outcome.reason}`);
      break;
  }
}

async function onSave(): Promise<void> {
  const tab = tabs.active();
  if (!tab) return;
  const text = tabs.source(tab.id);
  if (text === null) return;
  const stash = tabFileHandles.get(tab.id);
  // A scratch tab with no destination yet → ask for a name up front so
  // the UX is identical on Chromium (picker pre-filled) and Firefox
  // (download named). file: write-backs and stashed-handle re-saves
  // skip the prompt — they already know where to write.
  const isFirstScratchSave =
    tab.origin === 'scratch' && !stash;
  let suggestedName: string | undefined;
  if (isFirstScratchSave) {
    const chosen = await promptFilename({ title: 'Save', initial: ensureSasName(tab.title) });
    if (chosen === null) return; // cancelled — stay dirty
    suggestedName = chosen;
  }
  try {
    const outcome = await saveTab({
      text,
      tab,
      source: currentSource,
      fileHandle: stash,
      suggestedName,
    });
    await applySaveOutcome(tab, text, outcome);
  } catch (e) {
    output.error(`save failed: ${e instanceof Error ? e.message : String(e)}`);
  }
}

async function onSaveAs(): Promise<void> {
  const tab = tabs.active();
  if (!tab) return;
  const text = tabs.source(tab.id);
  if (text === null) return;
  const chosen = await promptFilename({
    title: 'Save as',
    initial: ensureSasName(tab.title),
    okLabel: 'Save',
  });
  if (chosen === null) return; // cancelled
  try {
    const outcome = await saveTab({
      text,
      tab,
      source: currentSource,
      suggestedName: chosen,
      forcePrompt: true,
    });
    await applySaveOutcome(tab, text, outcome);
  } catch (e) {
    output.error(`save failed: ${e instanceof Error ? e.message : String(e)}`);
  }
}

// Origin-aware rename triggered by double-clicking a tab title:
//   scratch → in-place title change (becomes the next Save suggestion;
//             no origin/dirty change)
//   file:   → Save As (the File System Access API has no portable
//             on-disk rename, so we write a renamed copy)
//   lesson  → no-op (read-only)
async function onRenameTab(id: string): Promise<void> {
  const tab = tabs.list().find((t) => t.id === id);
  if (!tab || tab.origin === 'lesson') return;
  if (tab.origin === 'file') {
    if (tabs.active()?.id !== id) tabs.activate(id);
    await onSaveAs();
    return;
  }
  const chosen = await promptFilename({
    title: 'Rename',
    initial: ensureSasName(tab.title),
    okLabel: 'Rename',
  });
  if (chosen === null) return;
  tabs.setTitle(id, chosen);
}

// Bind the editor-keymap shortcuts to the real implementations now that
// they exist.
saveActiveTab = () => void onSave();
saveActiveTabAs = () => void onSaveAs();

// --- tutorial mode --------------------------------------------------------
// The lessons pane is always wired (manifest + worker) but starts hidden.
// Entry points are the Learn affordance, the `Open tutorial` palette
// command, and a first-access toast (no toolbar button anymore).
// `?mode=tutorial` and `localStorage:kolistat:mode` are honoured for deep
// links and persistence.

const TUTORIAL_MODE_KEY = 'kolistat:mode';
// Set once the first-access tutorial toast has been shown + acted on
// (either action dismisses it), so it never reappears.
const TUTORIAL_PROMPT_KEY = 'kolistat:tutorial-prompted';
const params = new URLSearchParams(window.location.search);
const tutorialDeepLink = params.get('mode') === 'tutorial';
const initialTutorial =
  tutorialDeepLink
  || localStorage.getItem(TUTORIAL_MODE_KEY) === 'tutorial';

const appEl = document.getElementById('app');
const railEl = document.getElementById('lessons-rail');

// Mirrors `appEl.dataset.mode`; the Learn toggle reads it to decide
// open-vs-close. Kept in sync only through `setTutorialMode`, the single
// chokepoint every entry point (toast / palette / boot / Learn) routes
// through.
let tutorialActive = false;

function setTutorialMode(active: boolean): void {
  tutorialActive = active;
  if (appEl) appEl.dataset.mode = active ? 'tutorial' : 'editor';
  if (railEl) railEl.hidden = !active;
  localStorage.setItem(TUTORIAL_MODE_KEY, active ? 'tutorial' : 'editor');
  // Keep the Learn toggle's icon/label/aria-pressed honest regardless of
  // which entry point flipped the mode.
  toolbarApi?.setLearnActive(active);
}

// Single tutorial entry point: switch the layout, then open the
// last/first lesson (no-op if the tutorial hasn't finished attaching —
// the layout still flips and the rail renders).
function enterTutorial(): void {
  setTutorialMode(true);
  void tutorialApi?.openFirst();
}

setTutorialMode(initialTutorial);

/// Re-render the account widget. Draw the known state at once, then
/// add the reminder row once the pending check answers. The check
/// costs one GET /terms per page load, cached by fetchTermsInfo.
async function refreshAccountWidget(): Promise<void> {
  const user = getUser();
  toolbarApi?.refreshAccount(user ? { email: user.email, termsPending: false } : null);
  if (!user) return;
  const pending = await termsPending();
  if (!pending || getUser()?.email !== user.email) return;
  toolbarApi?.refreshAccount({ email: user.email, termsPending: true });
}

if (toolbarEl) {
  toolbarApi = attachToolbar({
    host: toolbarEl,
    onRun,
    onReset,
    onNew,
    onSave,
    onSaveAs,
    onPickFolder,
    onParseAll,
    onShareLink: onCopyShareLink,
    // Track the current theme so newly-built EditorStates pick the
    // right compartment value, plus dispatch the live reconfigure
    // for the active state.
    onThemeChange: (theme) => {
      currentTheme = theme;
      applyEditorTheme(theme);
    },
    // Toggle: close when the tutorial is already open, otherwise open it
    // on the first lesson.
    onLearn: () => { if (tutorialActive) setTutorialMode(false); else enterTutorial(); },
    onSignIn: () => openLoginModal(),
    onSignOut: () => {
      // Fire-and-forget: the Worker POST is best-effort, local wipe +
      // listener notification happens inside signOut() before the
      // network resolves. The auth-state listener wired below
      // re-renders the toolbar account widget, so no manual reload
      // needed — runner caches re-read getLicenseContext per call.
      void signOut();
    },
    onAcceptTerms: () => {
      void requireTermsAccepted().then(() => refreshAccountWidget());
    },
  });
  void refreshAccountWidget();
  // setTutorialMode(initialTutorial) above ran before the toolbar
  // existed, so push the boot mode onto the Learn toggle now.
  toolbarApi.setLearnActive(tutorialActive);
  // Auth-state changes (refresh swap, sign-in via redirect capture or
  // from another tab, sign-out, DEV escape hatch) re-render the account
  // widget and keep the required sign-in dialog in step with the state.
  onAuthStateChange(() => {
    if (isAuthenticated()) closeLoginModal();
    else openLoginModal('required');
    void refreshAccountWidget();
    void maybePromptTerms();
  });
  startAuthLifecycle();
  if (!isAuthenticated()) openLoginModal('required');
  void maybePromptTerms();
  // SEC-004 follow-up: if the Worker bounced us back with an auth
  // error (e.g. `?error=account_exists&reason=different_provider`),
  // open the login modal pre-populated with an explanation so the
  // user isn't stranded on a blank screen.
  if (authError) showLoginError(authError);
  // Sync save-enabled to whatever tab is already active by the time
  // the toolbar attaches (a restored lesson tab needs Save off).
  const initialTab = tabs.active();
  if (initialTab?.origin === 'lesson') {
    toolbarApi.setSaveEnabled(false, 'Lessons are read-only — use New to copy out.');
  }
}

// Deep-link autorun: only when the link asked for it (`run=1`); the
// default is prefill-only so the reader sees the code before it runs.
// The engine is already up (`loadKolilang()` is awaited at module top),
// so this fires immediately on the normal run path. A signed-out
// visitor already sees the required dialog; the link is kept and runs
// after the sign-in (see the boot block). We run the captured code
// directly rather than the active doc so it stays correct even if
// `?mode=tutorial` opened a lesson tab.
if (deepLink?.run && ensureSignedIn()) {
  void runProgram(kolilang, deepLink.code, output, onRunBlocked)
    .catch((e) => {
      output.error(`runner failed: ${e instanceof Error ? e.message : String(e)}`);
    })
    .finally(() => void refreshWorkLibrary());
}

if (railEl) {
  attachTutorial({
    rail: railEl,
    output,
    kolilang,
    openLessonInTab: (id, title, source) => {
      // Preserve user edits across rail clicks — re-opening a
      // lesson focuses the existing tab without resetting source.
      // (A future "Reset lesson" button could pass replaceContent:
      // true if we want a one-click way back to the starter.)
      tabs.openOrFocus(`lesson:${id}`, title, 'lesson', source, false);
    },
    runActive: () => onRun(),
    setStatus(text) {
      if (crumbsEl) crumbsEl.textContent = text;
    },
  }).then((api) => {
    tutorialApi = api;
    // Sync the rail to whatever tab is already active by the time
    // the tutorial finishes attaching (likely the lesson tab the
    // tutorial just opened, but could be a restored file tab).
    api.notifyActiveTab(tabs.active()?.id ?? null);
    // Palette entry points — registered here (not at module top) so
    // `tutorialApi` is non-null by the time they can run.
    commands.register({ id: 'tutorial-open', label: 'Open tutorial', run: enterTutorial });
    commands.register({ id: 'editor-mode', label: 'Exit tutorial', run: () => setTutorialMode(false) });
  }).catch((e) => {
    console.error('tutorial init failed', e);
  });
}

// --- first-access tutorial toast ------------------------------------------
// Offer the tutorial once per browser. Skipped on a `?mode=tutorial`
// deep-link (the user already opted in) and once the flag is set. Both
// actions set the flag so it never reappears. Sits below the dialogs
// (z-index 90) and is non-modal — it waits for the user.
function maybeShowTutorialToast(): void {
  if (tutorialDeepLink) return;
  let prompted: string | null = null;
  try {
    prompted = localStorage.getItem(TUTORIAL_PROMPT_KEY);
  } catch {
    // localStorage unavailable — skip the toast rather than risk a loop.
    return;
  }
  if (prompted) return;
  const markPrompted = (): void => {
    try { localStorage.setItem(TUTORIAL_PROMPT_KEY, '1'); } catch { /* best-effort */ }
  };
  showToast({
    message: 'New to KoliStat? Take the interactive SAS tutorial.',
    actions: [
      { label: 'Start tutorial', primary: true, onClick: () => { markPrompted(); enterTutorial(); } },
      { label: 'Dismiss', onClick: markPrompted },
    ],
  });
}
maybeShowTutorialToast();

// --- resizable sidebar ----------------------------------------------------
function attachResize(): void {
  const app = document.getElementById('app');
  const handle = document.getElementById('resize-handle');
  if (!app || !handle) return;

  const stored = localStorage.getItem('kolistat:sidebar-w');
  if (stored) app.style.setProperty('--sidebar-w', `${stored}px`);

  const onDown = (ev: MouseEvent) => {
    ev.preventDefault();
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const move = (m: MouseEvent) => {
      // Minimum mirrors the `minmax(300px, …)` enforced in CSS so
      // the lesson-actions row stays single-line at any drag width.
      const w = Math.max(300, Math.min(600, m.clientX));
      app.style.setProperty('--sidebar-w', `${w}px`);
    };
    const up = () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      const w = app.style.getPropertyValue('--sidebar-w').trim().replace('px', '');
      if (w) localStorage.setItem('kolistat:sidebar-w', w);
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  };
  handle.addEventListener('mousedown', onDown);
}
attachResize();
attachOutputResize();

// Drag the horizontal divider to resize the output panel. Persists the
// picked height in localStorage so the layout survives reloads.
function attachOutputResize(): void {
  const divider = document.getElementById('output-divider');
  const host = document.getElementById('output-host');
  const pane = document.getElementById('editor-pane');
  if (!divider || !host || !pane) return;

  const stored = localStorage.getItem('kolistat:output-h');
  if (stored) host.style.flex = `0 0 ${stored}px`;

  divider.addEventListener('mousedown', (ev) => {
    ev.preventDefault();
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
    const move = (m: MouseEvent) => {
      const paneRect = pane.getBoundingClientRect();
      const h = Math.max(80, Math.min(paneRect.height - 120, paneRect.bottom - m.clientY));
      host.style.flex = `0 0 ${h}px`;
    };
    const up = () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      const match = /0 0 (\d+)px/.exec(host.style.flex);
      if (match) localStorage.setItem('kolistat:output-h', match[1]);
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  });
}

async function openFileWithDiag(path: string, diag: { s: number; e: number } | null): Promise<void> {
  await openFile(path);
  if (diag) {
    const doc = view.state.doc;
    const from = Math.min(diag.s, doc.length);
    const to = Math.min(Math.max(diag.e, from + 1), doc.length);
    view.dispatch({
      selection: { anchor: from, head: to },
      scrollIntoView: true,
    });
    view.focus();
  }
}

async function loadSource(src: FolderSource): Promise<void> {
  currentSource = src;
  entriesCache = await src.list();
  // Hand the data entries to the registry. This drops the previous
  // folder's registrations but does not touch DuckDB otherwise.
  await attachDataSource(src, entriesCache);
  // Walk dirty `file:` tabs whose path no longer exists in the new
  // source root and downgrade them to `untitled:N` so unsaved edits
  // aren't silently orphaned. Clean file tabs whose path has gone
  // missing also get downgraded so re-save doesn't write through to
  // a path that resolves elsewhere.
  const known = new Set(entriesCache.map((e) => e.path));
  for (const t of tabs.list()) {
    if (!t.id.startsWith(FILE_TAB_PREFIX)) continue;
    const path = t.id.slice(FILE_TAB_PREFIX.length);
    if (known.has(path)) continue;
    const newId = nextUntitledId();
    const n = newId.slice(UNTITLED_TAB_PREFIX.length);
    tabs.rename(t.id, newId, `untitled-${n}.sas`);
  }
  if (treeEl) renderFileTree(treeEl, src, entriesCache, activePath, treeOpts());
  // Open the first file only if there isn't already a file: tab from
  // this folder active — boot-time auto-restore lands here with the
  // user's last-active tab already open and we shouldn't switch off it.
  const active = tabs.active();
  const activeFromThisFolder =
    active?.id.startsWith(FILE_TAB_PREFIX)
    && known.has(active.id.slice(FILE_TAB_PREFIX.length));
  const firstCode = entriesCache.find((e) => e.kind === 'code');
  if (firstCode && !activeFromThisFolder) {
    await openFile(firstCode.path);
  }
  if (crumbsEl) crumbsEl.textContent = `${src.label} (${entriesCache.length} files)`;
  // Persist the directory handle (Chromium only — no-op elsewhere)
  // so the next session can reconnect without re-prompting.
  if (src.rootHandle) {
    void saveRootHandle(src.rootHandle());
  }
}

async function openFile(path: string): Promise<void> {
  if (!currentSource) return;
  const text = await currentSource.read(path);
  // Path-keyed id: re-opening the same file focuses its tab instead
  // of stacking. Tab activation fires onActiveChange, which syncs
  // `activePath` and re-renders the tree highlight.
  const title = path.split('/').pop() ?? path;
  tabs.openOrFocus(`${FILE_TAB_PREFIX}${path}`, title, 'file', text);
  if (crumbsEl && currentSource) {
    crumbsEl.textContent = `${currentSource.label} ❯ ${path}`;
  }
}

/// Data files never become tabs. Loading registers the file with
/// DuckDB when needed, which is the download-and-decrypt step for cloud
/// sources; then the viewer reads it in place through its reader call.
async function openDataFile(path: string): Promise<void> {
  if (!currentSource) return;
  const entry = entriesCache.find((e) => e.path === path);
  try {
    await ensureDataLoaded(path);
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    // ensureDataLoaded registers every handle entry, not only this path.
    // A different, unreadable file can therefore fail the call while this
    // one loaded. Open the viewer when this file is in, and only warn.
    if (isDataLoaded(path)) {
      showToast({ message: `Some data files could not be loaded: ${msg}`, durationMs: 6000 });
    } else {
      showToast({ message: `Could not load ${path}: ${msg}`, durationMs: 6000 });
      return;
    }
  }
  // A local source shows no loaded dot. A re-render here would only
  // reset the tree's collapsed-folder state, with no visible change.
  const opts = treeOpts();
  if (treeEl && currentSource && opts.loaded) {
    renderFileTree(treeEl, currentSource, entriesCache, activePath, opts);
  }
  const viewer = await import('./dataset-viewer');
  await viewer.openDatasetViewer({
    relation: readerFor(path),
    title: path,
    subtitle: entry ? formatSize(entry.size) : undefined,
  });
}

if (treeEl) {
  treeEl.addEventListener(OPEN_EVENT, (ev) => {
    const detail = (ev as CustomEvent<OpenEventDetail>).detail;
    if (!detail?.path) return;
    if (detail.kind === 'data') void openDataFile(detail.path);
    else void openFile(detail.path);
  });
}

// Drag-and-drop a folder onto the window as a third-way import.
window.addEventListener('dragover', (ev) => ev.preventDefault());
window.addEventListener('drop', async (ev) => {
  ev.preventDefault();
  if (!ev.dataTransfer) return;
  const src = await DragDropSource.fromItems(ev.dataTransfer.items);
  if (src) await loadSource(src);
});

// --- boot-time auto-restore of the previously-picked folder ---------------
// IDB stash + already-granted permission → re-attach silently. Permission
// is NEVER re-requested at boot; a `'prompt'` state stays as-is until the
// user clicks Save (which happens inside a user gesture).
void (async () => {
  const handle = await loadRootHandle();
  if (!handle) return;
  const perm = await queryReadPermission(handle);
  if (perm !== 'granted') return;
  try {
    await loadSource(new DirectoryHandleSource(handle));
  } catch {
    // Folder vanished from disk — drop the stale handle so the next
    // boot doesn't loop on the same failure.
    await clearRootHandle();
  }
})();

// --- beforeunload guard ---------------------------------------------------
// Browsers ignore the message string and show a generic "Leave site?"
// prompt; setting `returnValue` is what triggers it.
window.addEventListener('beforeunload', (ev) => {
  if (tabs.anyDirty()) {
    ev.preventDefault();
    ev.returnValue = '';
  }
});
